import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import UserCredentialsFields from '../../common/UserCredentialsFields';
import {
  setUsersData,
  completeUserCredentialsTask,
  cancelUserCredentialsTask
} from '../../../redux/actions/rootAction';
import { selectPreparedState } from '../../../redux/selectors/preparedState';
import {
  buildUsersData,
  applyCustomRecipientsData,
  usersDataOk
} from '../../helpers/userFieldsData';
import {
  reportCredentialsCancelled,
  reportCredentialsDone
} from '../../common/userCredentialsSideEffects';

/**
 * Credentials fields for embedding in QES provider modals.
 * Host owns footer buttons; call submit() / cancel() via ref.
 */
const UserCredentialsStep = forwardRef(function UserCredentialsStep(
  props,
  ref
) {
  const { active, onValidityChange } = props;
  const dispatch = useDispatch();
  const fieldsRef = useRef(null);
  const [valid, setValid] = useState(false);

  const userCredentialsTask = useSelector(state => state.userCredentialsTask);
  const region = useSelector(state => state.region);
  const usersDataState = useSelector(state => state.usersData);
  const qesValueInheritance = useSelector(state => state.qesValueInheritance);
  const embeddingFieldsInheritance = useSelector(
    state => state.embeddingFieldsInheritance
  );
  const recipientConditions = useSelector(state => state.recipientConditions);
  const prepared = useSelector(selectPreparedState);

  const usersData = useMemo(() => {
    const built = buildUsersData({
      usersData: usersDataState,
      requestedDocuments: prepared.requestedDocuments,
      preparedFormsData: prepared.preparedFormsData,
      preparedFormsStructures: prepared.preparedFormsStructures,
      qesValueInheritance,
      embeddingFieldsInheritance,
      recipientConditions
    });
    return applyCustomRecipientsData(
      built,
      userCredentialsTask?.customRecipientsDataSource
    );
  }, [
    usersDataState,
    prepared.requestedDocuments,
    prepared.preparedFormsData,
    prepared.preparedFormsStructures,
    qesValueInheritance,
    embeddingFieldsInheritance,
    recipientConditions,
    userCredentialsTask?.customRecipientsDataSource
  ]);

  const handleValidity = isOk => {
    setValid(isOk);
    if (onValidityChange) onValidityChange(isOk);
  };

  useImperativeHandle(
    ref,
    () => ({
      isValid: () => valid,
      submit: () => {
        if (!fieldsRef.current?.isValid()) return false;
        const merged = fieldsRef.current.getMergedUsersData();
        reportCredentialsDone();
        dispatch(setUsersData(merged));
        dispatch(completeUserCredentialsTask());
        return true;
      },
      cancel: () => {
        reportCredentialsCancelled();
        dispatch(cancelUserCredentialsTask());
      }
    }),
    [valid, dispatch]
  );

  if (!active) {
    return null;
  }

  return (
    <UserCredentialsFields
      ref={fieldsRef}
      usersData={usersData}
      usersDataOk={usersDataOk}
      region={region}
      active={active}
      onValidityChange={handleValidity}
      maxHeight="50vh"
      margin="8px 0"
    />
  );
});

UserCredentialsStep.propTypes = {
  active: PropTypes.bool,
  onValidityChange: PropTypes.func
};

export default UserCredentialsStep;
